import React, { useState, useEffect } from 'react';
import { Baby, MapPin, Sparkles, ShieldCheck, Heart, LogOut, CheckCircle2 } from 'lucide-react';

// ✅ CANAL DE COMUNICACIÓN EN TIEMPO REAL (Broadcast API)
const canalTV = new BroadcastChannel('iglekids_tv');

const PantallaPasillo = ({ alCerrar }) => {
  const [bienvenidaActiva, setBienvenidaActiva] = useState(false);
  const [datosNino, setDatosNino] = useState(null);
  const [hora, setHora] = useState(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));

  useEffect(() => {
    // Reloj en tiempo real para el modo reposo
    const timer = setInterval(() => {
      setHora(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
    }, 1000);
    
    const activarPantalla = (data) => {
      if (data && data.nombre) {
        setDatosNino(data);
        setBienvenidaActiva(true);
        // Temporizador de 10 segundos antes de volver al reposo
        setTimeout(() => setBienvenidaActiva(false), 10000);
      }
    };

    // Escuchamos el canal de TV
    canalTV.onmessage = (event) => activarPantalla(event.data);

    // Respaldo de seguridad vía storage
    const manejarStorage = (e) => {
      if (e.key === 'ultimo_registro_tv' && e.newValue) {
        activarPantalla(JSON.parse(e.newValue));
      }
    };
    window.addEventListener('storage', manejarStorage);

    return () => {
      clearInterval(timer);
      window.removeEventListener('storage', manejarStorage);
      canalTV.close();
    };
  }, []);

  const esSalida = datosNino?.tipo === 'DESPEDIDA';

  return (
    <div className="h-screen w-full bg-[#0A2440] overflow-hidden font-sans relative text-white flex flex-col select-none">
      {/* Botón invisible de escape para el staff */}
      <button onClick={alCerrar} className="absolute top-0 left-0 w-32 h-32 opacity-0 z-[100] cursor-default" />

      {/* --- MODO REPOSO: MARCA Y RELOJ --- */}
      <div className={`absolute inset-0 transition-all duration-1000 ease-in-out flex flex-col ${bienvenidaActiva ? 'scale-110 blur-3xl opacity-0 pointer-events-none' : 'scale-100 opacity-100'}`}>
        <div className="p-12 md:p-20 flex justify-between items-start">
          <div className="flex items-center gap-10 text-left">
            <div className="flex flex-col border-r-4 border-white/10 pr-10">
              <h2 className="text-2xl md:text-4xl font-light tracking-[0.5em] leading-none uppercase">Casa de Fe</h2>
              <p className="text-xs font-black tracking-[0.8em] text-amber-400 mt-4 italic">VILLAHERMOSA</p>
            </div>
            <div className="flex items-center gap-6">
              <div className="bg-amber-500 p-4 rounded-[2.5rem] shadow-xl">
                <ShieldCheck size={50} className="text-[#0A2440]" />
              </div>
              <h1 className="text-5xl md:text-8xl font-black italic tracking-tighter uppercase leading-none text-white drop-shadow-2xl">IgleKids</h1>
            </div>
          </div>
          <div className="text-6xl md:text-[10rem] font-extralight tracking-tighter tabular-nums opacity-80">{hora}</div>
        </div>

        <div className="flex-1 flex flex-col items-center justify-center space-y-12 -mt-20">
          <div className="relative">
            <Sparkles size={160} className="text-amber-500/10 animate-pulse" />
            <div className="absolute inset-0 bg-amber-400/5 blur-3xl rounded-full" />
          </div>
          <h2 className="text-3xl md:text-6xl font-thin uppercase tracking-[1.5em] text-white/10 animate-pulse">Bienvenidos</h2>
        </div>
      </div>

      {/* --- MODO INTERACTIVO: PANTALLA DE ACCIÓN --- */}
      <div className={`absolute inset-0 z-50 flex items-center justify-center transition-all duration-700 ease-out ${bienvenidaActiva ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0 pointer-events-none'}`}>
        
        <div className={`absolute inset-0 transition-colors duration-1000 ${esSalida ? 'bg-[#064e3b]' : 'bg-[#0A2440]'}`} />
        
        <div className="relative bg-white w-[96%] h-[92%] rounded-[4rem] md:rounded-[6rem] shadow-2xl overflow-hidden flex flex-col md:flex-row animate-in zoom-in-95 duration-700">
           
           <div className={`w-full md:w-[35%] flex flex-col items-center justify-center p-10 relative transition-colors duration-1000 ${esSalida ? 'bg-[#064e3b]' : 'bg-[#0A2440]'}`}>
              <div className={`absolute top-0 left-0 w-full h-4 md:h-6 ${esSalida ? 'bg-emerald-400' : 'bg-amber-500'}`} />
              
              <div className="w-56 h-56 md:w-80 lg:w-[26rem] md:h-80 lg:h-[26rem] bg-white rounded-[4rem] md:rounded-[6rem] flex items-center justify-center shadow-2xl mb-10 relative border-[12px] border-white/20">
                 {esSalida ? 
                    <LogOut size={220} className="text-[#064e3b] w-[60%]" strokeWidth={1} /> : 
                    <Baby size={240} className="text-[#0A2440] w-[60%]" strokeWidth={1} />
                 }
                 <div className={`absolute bottom-0 w-full py-5 flex justify-center ${esSalida ? 'bg-emerald-400' : 'bg-amber-500'}`}>
                    <Heart size={40} className={esSalida ? 'text-[#064e3b]' : 'text-[#0A2440]'} fill="currentColor" />
                 </div>
              </div>
              
              <p className={`${esSalida ? 'text-emerald-400' : 'text-amber-500'} font-black text-sm md:text-xl uppercase tracking-[0.5em] text-center italic`}>
                {esSalida ? 'Salida Segura' : 'Ingreso Exitoso'}
              </p>
           </div>

           <div className="flex-1 p-12 md:p-20 lg:p-24 flex flex-col justify-center space-y-10 md:space-y-16">
              <div className="space-y-6">
                 <div className="flex items-center gap-6">
                    <span className={`h-1 w-20 md:w-32 rounded-full ${esSalida ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                    <h3 className="text-2xl md:text-5xl font-light text-slate-300 uppercase tracking-[0.4em] italic text-left">
                      {esSalida ? '¡Nos vemos pronto!' : '¡Hola, bienvenido!'}
                    </h3>
                 </div>
                 
                 <h2 className={`font-black italic tracking-tighter leading-[0.85] text-left transition-colors duration-1000 uppercase break-words line-clamp-1 ${esSalida ? 'text-[#064e3b]' : 'text-[#0A2440]'}`}
                     style={{ fontSize: 'clamp(3rem, 14vw, 15rem)' }}>
                    {datosNino?.nombre.split(' ')[0]}
                 </h2>
              </div>

              <div className="flex items-center gap-8 md:gap-14 bg-slate-50 p-8 md:p-14 lg:p-16 rounded-[4rem] md:rounded-[5rem] border-2 border-slate-100 shadow-inner relative overflow-hidden">
                 <div className={`w-24 h-24 md:w-40 md:h-40 rounded-[2.5rem] md:rounded-[3.5rem] flex items-center justify-center text-white shadow-2xl shrink-0 transform rotate-3 ${esSalida ? 'bg-emerald-600' : 'bg-[#0A2440]'}`}>
                    {esSalida ? <CheckCircle2 size={80} /> : <MapPin size={80} className="text-amber-400" />}
                 </div>
                 
                 <div className="min-w-0 flex-1 text-left">
                    <p className="text-sm md:text-3xl font-black text-slate-400 uppercase tracking-[0.3em] mb-2 italic">
                      {esSalida ? 'Protocolo de:' : 'Tu salón es:'}
                    </p>
                    <p className={`font-black italic tracking-tighter leading-none truncate ${esSalida ? 'text-slate-600' : 'text-[#0A2440]'}`}
                       style={{ fontSize: 'clamp(2rem, 8vw, 8rem)' }}>
                       {esSalida ? 'Regreso a Casa' : datosNino?.salon}
                    </p>
                 </div>
                 <Sparkles className="absolute right-[-20px] bottom-[-20px] text-slate-200/50" size={150} />
              </div>
           </div>
        </div>
      </div>
    </div>
  );
};

export default PantallaPasillo;